import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Coffee, Sparkles, Gift, ArrowRight, Calendar, Lightbulb, Clock, ChevronRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_sip-schedule-admin/artifacts/vd09zrou_Logo%20-%20Final.png";
const HERO_IMAGE_URL = "https://static.prod-images.emergentagent.com/jobs/3fcf4473-53e6-4204-b317-124f4d0df193/images/ac1706c84f65a78f96fcd3c72d3e74db347e161c697d5fd1f6e5166eead639e7.jpeg";

const defaultDrinkImages = [
  { id: "1", url: "https://customer-assets.emergentagent.com/job_sip-schedule-admin/artifacts/enimm7jy_CoCo%20Berry%20Twist.png", label: "Yum!", color: "pink" },
  { id: "2", url: "https://customer-assets.emergentagent.com/job_sip-schedule-admin/artifacts/o1964de5_Sweetheart%20Soda.jpg", label: "Sweet!", color: "blue" },
  { id: "3", url: "https://customer-assets.emergentagent.com/job_sip-schedule-admin/artifacts/s97p0s5x_Scooby%20Snack.png", label: "Fun!", color: "yellow" },
  { id: "4", url: "https://customer-assets.emergentagent.com/job_sip-schedule-admin/artifacts/4oxugxrr_RB%20Float.png", label: "Tasty!", color: "navy" }
];

const colorClasses = {
  pink: { border: "border-sip-pink", bg: "bg-sip-pink", text: "text-white" },
  blue: { border: "border-sip-blue", bg: "bg-sip-blue", text: "text-white" },
  yellow: { border: "border-sip-yellow", bg: "bg-sip-yellow", text: "text-sip-navy" },
  navy: { border: "border-sip-navy", bg: "bg-sip-navy", text: "text-white" }
};

const features = [
  { icon: MapPin, title: 'Find Us', description: 'Check our schedule to see where we\'ll be!', link: '/schedule', color: 'bg-sip-blue' },
  { icon: Coffee, title: 'Our Menu', description: 'Discover our handcrafted drinks!', link: '/menu', color: 'bg-sip-navy' },
  { icon: Sparkles, title: 'Seasonal', description: 'Limited-time flavors!', link: '/seasonal', color: 'bg-sip-pink' },
  { icon: Gift, title: 'Rewards', description: 'Buy 10, get 11th FREE!', link: '/rewards', color: 'bg-sip-yellow' }
];

export const HomePage = () => {
  const [locations, setLocations] = useState([]);
  const [drinkImages, setDrinkImages] = useState(defaultDrinkImages);

  const fetchData = useCallback(async () => {
    try {
      // Fetch locations
      const locResponse = await fetch(`${API}/locations`);
      const locData = await locResponse.json();
      setLocations(locData.slice(0, 5)); // Get up to 5 upcoming locations

      // Fetch homepage settings
      const settingsResponse = await fetch(`${API}/homepage-settings`);
      const settingsData = await settingsResponse.json();
      if (settingsData.drink_images && settingsData.drink_images.length > 0) {
        setDrinkImages(settingsData.drink_images);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Navbar />
      
      {/* Hero Section - Full-width Image Banner */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* Hero Image */}
        <div className="relative w-full h-[50vh] md:h-[60vh] lg:h-[70vh]">
          <img 
            src={HERO_IMAGE_URL} 
            alt="Sip Fantasia Mobile Beverage Truck with colorful drinks" 
            className="w-full h-full object-cover"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          
          {/* Content Overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-8 md:pb-12 px-4">
            {/* Tagline */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="text-center"
            >
              <h1 className="font-script text-3xl md:text-4xl lg:text-5xl text-white drop-shadow-lg">
                Good Vibes. Great Sips!
              </h1>
              <p className="text-base md:text-lg text-white/90 mt-2 max-w-xl mx-auto drop-shadow">
                Serving bold flavors and big smiles - One fun sip at a time.
              </p>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              className="flex flex-wrap gap-3 justify-center mt-5"
            >
              <Link to="/schedule" className="bg-sip-yellow text-sip-navy font-semibold px-6 py-3 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all inline-flex items-center gap-2" data-testid="hero-cta-schedule">
                Find Us <MapPin size={18} />
              </Link>
              <Link to="/menu" className="bg-white/90 backdrop-blur text-sip-navy font-semibold px-6 py-3 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all inline-flex items-center gap-2" data-testid="hero-cta-menu">
                See Menu <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Next Stops Section - More Prominent */}
      {locations.length > 0 && (
        <section className="bg-gradient-to-r from-sip-navy via-sip-navy/95 to-sip-navy py-8" data-testid="next-stops-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-sip-yellow p-2 rounded-full">
                  <Clock className="w-6 h-6 text-sip-navy" />
                </div>
                <h2 className="font-script text-2xl md:text-3xl text-white">Next Stops</h2>
              </div>
              <Link to="/schedule" className="text-sip-yellow hover:text-white transition-colors flex items-center gap-1 text-sm font-medium">
                View All <ChevronRight size={16} />
              </Link>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {locations.map((location, index) => (
                <motion.div
                  key={location.location_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="bg-sip-yellow/20 text-sip-yellow p-2 rounded-lg shrink-0">
                      <MapPin size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white truncate">{location.name}</h3>
                      <p className="text-white/70 text-sm truncate">{location.address}</p>
                      <div className="flex items-center gap-2 mt-2 text-sip-yellow text-sm">
                        <Calendar size={14} />
                        <span>{location.date}</span>
                        <span>•</span>
                        <span>{location.time_start} - {location.time_end}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quick Links Grid */}
      <section className="py-12 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={feature.link} className="card-graffiti block h-full group text-center" data-testid={`feature-card-${feature.title.toLowerCase().replace(' ', '-')}`}>
                    <div className={`${feature.color} w-12 h-12 rounded-xl flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform`}>
                      <Icon className="text-white" size={22} />
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900">{feature.title}</h3>
                    <p className="text-gray-600 text-sm mt-1">{feature.description}</p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Share Your Flavor */}
      <section className="py-12 bg-gradient-to-br from-sip-pink/10 via-white to-sip-blue/10" data-testid="flavor-fantasia-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-sip-pink to-sip-yellow rounded-full mb-4">
              <Lightbulb className="w-7 h-7 text-white" />
            </div>
            <h2 className="font-script text-3xl md:text-4xl text-sip-navy mb-3">Share Your Flavor Fantasia!</h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">Got a dream flavor combo? Share it with us and the community!</p>
            <Link to="/flavor-fantasia" className="bg-gradient-to-r from-sip-pink to-sip-yellow text-white font-semibold px-6 py-3 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all inline-flex items-center gap-2" data-testid="flavor-fantasia-cta">
              Share Your Idea <Sparkles size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Book Us CTA */}
      <section className="py-12 bg-sip-navy" data-testid="events-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
            <Calendar className="w-12 h-12 text-sip-yellow mx-auto mb-4" />
            <h2 className="font-script text-3xl md:text-4xl text-white mb-3">Book Us For Your Event!</h2>
            <p className="text-white/80 mb-6">Birthdays, weddings, corporate events - we bring the party to you!</p>
            <Link to="/events" className="btn-accent inline-flex items-center gap-2" data-testid="events-cta-button">
              Book Now <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
